import { useMemo, useState } from 'react';
import { createFileRoute, useRouter, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Sheet, SheetContent } from '@/components/atoms/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DefinitionFormContent,
  type CreateDefinitionPayload,
  type UpdateDefinitionPayload,
} from '@/components/organisms/custom-fields/CustomFieldDefinitionForm';
import {
  CustomFieldsList,
  type CustomFieldCardEntityFilter,
} from '@/components/organisms/custom-fields/CustomFieldsList';
import { DetailPageLayout } from '@/components/layouts';
import { useCustomFieldDefinitions } from '@/hooks/custom-fields/useCustomFieldDefinitions';
import { useCustomFieldSchema } from '@/hooks/custom-fields/useCustomFieldSchema';
import { useDeleteDefinition } from '@/hooks/custom-fields/useDeleteDefinition';
import { apiDelete, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import {
  activeAssociationForDisplay,
  associationPatchFromDraft,
  emptyMeta,
  entityUsageDraftsFromMeta,
  jobTypeIdsEqual,
  jobTypeIdsForSave,
  ENTITY_TYPE_LABELS,
  FIELD_TYPE_LABELS,
  type DeleteTarget,
  type EntityUsageDraft,
  type FieldMeta,
} from '@/lib/custom-fields/fieldModel';
import { toast } from 'sonner';
import type {
  CreateAssociationDto,
  CreateInheritanceRuleDto,
  CustomFieldAssociation,
  CustomFieldDefinition,
  CustomFieldEntityType,
  CustomFieldInheritanceRule,
  CustomFieldSchema,
  UpdateAssociationDto,
  UpdateDefinitionDto,
} from '@/types/custom-fields';

export const Route = createFileRoute('/_dashboard/dashboard/settings/customfields')({
  component: CustomFieldsPage,
});

const CUSTOM_FIELD_NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/customfields\/new\/?$/;
const CUSTOM_FIELD_EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/customfields\/([^/]+)\/edit\/?$/;

function CustomFieldsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { location } = useRouterState();
  const { data: definitions = [], isLoading } = useCustomFieldDefinitions();
  const deleteDef = useDeleteDefinition();

  const contactSchemaQuery = useCustomFieldSchema('contact', true);
  const addressSchemaQuery = useCustomFieldSchema('address', true);
  const estimateSchemaQuery = useCustomFieldSchema('estimate', true);
  const invoiceSchemaQuery = useCustomFieldSchema('invoice', true);
  const jobSchemaQuery = useCustomFieldSchema('job', true);
  const equipmentSchemaQuery = useCustomFieldSchema('equipment_description', true);

  const schemasReady = [
    contactSchemaQuery,
    addressSchemaQuery,
    estimateSchemaQuery,
    invoiceSchemaQuery,
    jobSchemaQuery,
    equipmentSchemaQuery,
  ].every((query) => query.isSuccess);

  const { data: contactSchemas = [] } = contactSchemaQuery;
  const { data: addressSchemas = [] } = addressSchemaQuery;
  const { data: estimateSchemas = [] } = estimateSchemaQuery;
  const { data: invoiceSchemas = [] } = invoiceSchemaQuery;
  const { data: jobSchemas = [] } = jobSchemaQuery;
  const { data: equipmentSchemas = [] } = equipmentSchemaQuery;

  const createOpen = CUSTOM_FIELD_NEW_ROUTE_PATTERN.test(location.pathname);
  const editDefinitionId = location.pathname.match(CUSTOM_FIELD_EDIT_ROUTE_PATTERN)?.[1];
  const decodedEditDefinitionId = editDefinitionId
    ? decodeURIComponent(editDefinitionId)
    : undefined;

  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState<CustomFieldCardEntityFilter>('all');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [drawerDirty, setDrawerDirty] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [isSavingCreate, setIsSavingCreate] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const allSchemas = useMemo(
    () => [
      ...contactSchemas,
      ...addressSchemas,
      ...estimateSchemas,
      ...invoiceSchemas,
      ...jobSchemas,
      ...equipmentSchemas,
    ],
    [addressSchemas, contactSchemas, estimateSchemas, invoiceSchemas, jobSchemas, equipmentSchemas],
  );

  const metaByDefinitionId = useMemo(() => {
    const map = new Map<string, FieldMeta>();
    definitions.forEach((definition) => {
      map.set(definition.id, emptyMeta(definition));
    });

    allSchemas.forEach((schema: CustomFieldSchema) => {
      const current = map.get(schema.definition.id) ?? emptyMeta(schema.definition);
      if (!current.associations.some((association) => association.id === schema.association.id)) {
        current.associations.push(schema.association);
      }
      schema.inheritanceRules.forEach((rule) => {
        if (!current.inheritanceRules.some((existingRule) => existingRule.id === rule.id)) {
          current.inheritanceRules.push(rule);
        }
      });
      map.set(schema.definition.id, current);
    });

    map.forEach((meta) => {
      meta.associations.sort((a, b) => a.displayOrder - b.displayOrder);
      meta.inheritanceRules.sort((a, b) => a.priority - b.priority);
    });

    return map;
  }, [allSchemas, definitions]);

  const fieldRows = useMemo(
    () =>
      definitions.map(
        (definition) => metaByDefinitionId.get(definition.id) ?? emptyMeta(definition),
      ),
    [definitions, metaByDefinitionId],
  );

  const editDefinition = useMemo(
    () => definitions.find((definition) => definition.id === decodedEditDefinitionId) ?? null,
    [decodedEditDefinitionId, definitions],
  );
  const editMeta = editDefinition
    ? (metaByDefinitionId.get(editDefinition.id) ?? emptyMeta(editDefinition))
    : null;

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return fieldRows.filter((meta) => {
      const searchable = [
        meta.definition.label,
        meta.definition.fieldKey,
        meta.definition.description ?? '',
        FIELD_TYPE_LABELS[meta.definition.fieldType],
        ...meta.associations.map((association) => ENTITY_TYPE_LABELS[association.entityType]),
      ]
        .join(' ')
        .toLowerCase();
      const matchesSearch = !query || searchable.includes(query);
      const matchesEntity =
        entityFilter === 'all' ||
        meta.associations
          .filter(activeAssociationForDisplay)
          .some((association) => association.entityType === entityFilter);
      return matchesSearch && matchesEntity;
    });
  }, [entityFilter, fieldRows, search]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const labelResult = a.definition.label.localeCompare(b.definition.label);
      if (labelResult !== 0) return labelResult;
      return a.definition.fieldKey.localeCompare(b.definition.fieldKey);
    });
  }, [filteredRows]);

  const isDefinitionPending = isSavingCreate || isSavingEdit;
  const isUsagePending = isSavingCreate || isSavingEdit;

  function openCreateDrawer() {
    void router.navigate({ to: '/dashboard/settings/customfields/new' });
  }

  function openEditDrawer(definitionId: string) {
    void router.navigate({
      to: '/dashboard/settings/customfields/$id/edit',
      params: { id: definitionId },
    });
  }

  function closeDefinitionDrawer() {
    setDrawerDirty(false);
    setDiscardDialogOpen(false);
    void router.navigate({ to: '/dashboard/settings/customfields' });
  }

  function requestCloseDefinitionDrawer() {
    if (drawerDirty) {
      setDiscardDialogOpen(true);
      return;
    }
    closeDefinitionDrawer();
  }

  async function handleCreateDefinition(payload: CreateDefinitionPayload) {
    setIsSavingCreate(true);
    let created: CustomFieldDefinition | null = null;
    let shouldOpenEditOnFailure = false;
    try {
      created = await apiPost<CustomFieldDefinition, CreateDefinitionPayload['definition']>(
        operationsClient,
        customFieldEndpoints.definitions(),
        payload.definition,
      );

      if (!payload.isActive) {
        await apiPatch<CustomFieldDefinition, UpdateDefinitionDto>(
          operationsClient,
          customFieldEndpoints.definition(created.id),
          { isActive: false },
        );
      }

      const usage = payload.usage.map((draft) => normalizeDraftForSave(draft, payload.usage));
      for (const draft of usage) {
        await apiPost<CustomFieldAssociation, CreateAssociationDto>(
          operationsClient,
          customFieldEndpoints.definitionAssociations(created.id),
          createAssociationDtoFromDraft(draft),
        );
      }
      for (const draft of usage) {
        if (!draft.source) continue;
        await createRule(created.id, draft);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customFieldKeys.definitions() }),
        queryClient.invalidateQueries({ queryKey: customFieldKeys.all() }),
        queryClient.invalidateQueries({ queryKey: customFieldKeys.inheritanceRules() }),
      ]);
      toast.success('Custom field created');
      closeDefinitionDrawer();
    } catch (error) {
      shouldOpenEditOnFailure = Boolean(created?.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customFieldKeys.definitions() }),
        queryClient.invalidateQueries({ queryKey: customFieldKeys.all() }),
        queryClient.invalidateQueries({ queryKey: customFieldKeys.inheritanceRules() }),
      ]);
      toast.error(error instanceof Error ? error.message : 'Could not create custom field');
    } finally {
      setIsSavingCreate(false);
      if (shouldOpenEditOnFailure && created?.id) {
        openEditDrawer(created.id);
      }
    }
  }

  async function handleUpdateDefinition(definitionId: string, payload: UpdateDefinitionPayload) {
    const meta = editMeta;
    if (!meta) return;

    setIsSavingEdit(true);
    try {
      await saveCustomFieldDraft(definitionId, meta, payload);
      toast.success('Custom field updated');
      closeDefinitionDrawer();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update custom field');
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customFieldKeys.definitions() }),
        queryClient.invalidateQueries({ queryKey: customFieldKeys.all() }),
        queryClient.invalidateQueries({ queryKey: customFieldKeys.inheritanceRules() }),
      ]);
      setIsSavingEdit(false);
    }
  }

  async function handleDeleteTarget() {
    if (!deleteTarget) return;
    if (deleteTarget.type !== 'definition') {
      setDeleteTarget(null);
      return;
    }
    deleteDef.mutate(deleteTarget.id, {
      onSuccess: () => {
        if (decodedEditDefinitionId === deleteTarget.id) closeDefinitionDrawer();
        setDeleteTarget(null);
      },
    });
  }

  return (
    <DetailPageLayout
      header={
        <>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold">Custom Fields</h1>
            <p className="truncate text-sm text-muted-foreground">
              Create reusable fields, choose where they appear, and how they&apos;re filled in.
            </p>
          </div>
          <Button size="sm" onClick={openCreateDrawer}>
            <Plus className="h-4 w-4" />
            Add field
          </Button>
        </>
      }
    >
      <CustomFieldsList
        rows={sortedRows}
        sourceCount={fieldRows.length}
        totalCount={sortedRows.length}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        entityFilter={entityFilter}
        onEntityFilterChange={setEntityFilter}
        onCreate={openCreateDrawer}
        onEdit={(definitionId) => openEditDrawer(definitionId)}
        onDelete={(meta) =>
          setDeleteTarget({
            type: 'definition',
            id: meta.definition.id,
            label: meta.definition.label,
          })
        }
      />

      <Sheet
        open={createOpen || Boolean(decodedEditDefinitionId)}
        onOpenChange={(open) => {
          if (!open) requestCloseDefinitionDrawer();
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl"
          showCloseButton={false}
        >
          {createOpen ? (
            <DefinitionFormContent
              // Force a fresh form per definition (or create) so the form
              // initializes with the right `defaultValues` directly rather
              // than being reset post-mount — the reset path triggers the
              // Radix Select "value clears to ''" race fixed in NOT-181.
              // We key by `editDefinition?.id` so the form remounts once
              // the fetched definition resolves (handles the direct-URL /
              // refresh case where the definitions list loads after mount).
              key={
                createOpen
                  ? 'create'
                  : editDefinition
                    ? `edit:${editDefinition.id}`
                    : `edit-loading:${decodedEditDefinitionId ?? ''}`
              }
              mode={createOpen ? 'create' : 'edit'}
              definition={editDefinition}
              meta={editMeta}
              isPending={isDefinitionPending || isUsagePending}
              onClose={requestCloseDefinitionDrawer}
              onDirtyChange={setDrawerDirty}
              onCreate={handleCreateDefinition}
              onUpdate={handleUpdateDefinition}
            />
          ) : decodedEditDefinitionId ? (
            schemasReady && editDefinition ? (
              <DefinitionFormContent
                // Force a fresh form per definition (or create) so the form
                // initializes with the right `defaultValues` directly rather
                // than being reset post-mount — the reset path triggers the
                // Radix Select "value clears to ''" race fixed in NOT-181.
                // We key by `editDefinition?.id` so the form remounts once
                // the fetched definition resolves (handles the direct-URL /
                // refresh case where the definitions list loads after mount).
                key={
                  editDefinition
                    ? `edit:${editDefinition.id}`
                    : `edit-loading:${decodedEditDefinitionId ?? ''}`
                }
                mode="edit"
                definition={editDefinition}
                meta={editMeta}
                isPending={isDefinitionPending || isUsagePending}
                onClose={requestCloseDefinitionDrawer}
                onDirtyChange={setDrawerDirty}
                onCreate={handleCreateDefinition}
                onUpdate={handleUpdateDefinition}
              />
            ) : isLoading || !schemasReady ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2
                  className="h-5 w-5 animate-spin text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-muted-foreground">This custom field no longer exists.</p>
                <Button variant="outline" onClick={requestCloseDefinitionDrawer}>
                  Close
                </Button>
              </div>
            )
          ) : null}
        </SheetContent>
      </Sheet>

      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your unsaved custom field changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={closeDefinitionDrawer}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.label}?</DialogTitle>
            <DialogDescription>
              This will remove the selected custom field configuration. Definitions with saved
              values may be deactivated instead of deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTarget}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailPageLayout>
  );
}

export async function saveCustomFieldDraft(
  definitionId: string,
  meta: FieldMeta,
  payload: UpdateDefinitionPayload,
) {
  if (payload.definitionDirty) {
    await apiPatch<unknown, UpdateDefinitionDto>(
      operationsClient,
      customFieldEndpoints.definition(definitionId),
      payload.definition,
    );
  }

  const originalDrafts = entityUsageDraftsFromMeta(meta);
  const originalByEntity = new Map(originalDrafts.map((draft) => [draft.entityType, draft]));
  const associationByEntity = new Map(
    meta.associations
      .filter(activeAssociationForDisplay)
      .map((association) => [association.entityType, association]),
  );
  const nextDrafts = payload.usage.map((draft) => normalizeDraftForSave(draft, payload.usage));
  const nextByEntity = new Map(nextDrafts.map((draft) => [draft.entityType, draft]));

  for (const originalDraft of originalDrafts) {
    if (nextByEntity.has(originalDraft.entityType)) continue;
    await deleteRules(rulesForTarget(meta.inheritanceRules, originalDraft.entityType));
    const associationId =
      originalDraft.associationId ?? associationByEntity.get(originalDraft.entityType)?.id;
    if (associationId) {
      await apiDelete<void>(operationsClient, customFieldEndpoints.association(associationId));
    }
  }

  const newDrafts = nextDrafts.filter((draft) => !originalByEntity.has(draft.entityType));
  for (const draft of newDrafts) {
    await apiPost<CustomFieldAssociation, CreateAssociationDto>(
      operationsClient,
      customFieldEndpoints.definitionAssociations(definitionId),
      createAssociationDtoFromDraft(draft),
    );
  }
  for (const draft of newDrafts) {
    if (!draft.source) continue;
    await createRule(definitionId, draft);
  }

  const existingDrafts = nextDrafts.filter((draft) => originalByEntity.has(draft.entityType));
  for (const draft of existingDrafts) {
    const association = associationByEntity.get(draft.entityType);
    if (!association || !associationNeedsPatch(association, draft)) continue;
    await apiPatch<CustomFieldAssociation, UpdateAssociationDto>(
      operationsClient,
      customFieldEndpoints.association(association.id),
      updateAssociationDtoFromDraft(draft),
    );
  }

  for (const draft of existingDrafts) {
    await syncRulesForExistingDraft(definitionId, meta.inheritanceRules, draft);
  }
}

function normalizeDraftForSave(
  draft: EntityUsageDraft,
  allDrafts: EntityUsageDraft[],
): EntityUsageDraft {
  if (!draft.source) return draft;

  const sourceExists = allDrafts.some((item) => item.entityType === draft.source?.sourceEntityType);
  if (!sourceExists) return { ...draft, source: null };

  return draft;
}

function createAssociationDtoFromDraft(draft: EntityUsageDraft): CreateAssociationDto {
  return {
    entityType: draft.entityType,
    displayOrder: 0,
    jobTypeIds: jobTypeIdsForSave(draft),
    ...associationPatchFromDraft(draft),
  };
}

function updateAssociationDtoFromDraft(draft: EntityUsageDraft): UpdateAssociationDto {
  return {
    jobTypeIds: jobTypeIdsForSave(draft),
    ...associationPatchFromDraft(draft),
  };
}

function associationNeedsPatch(
  association: CustomFieldAssociation,
  draft: EntityUsageDraft,
): boolean {
  const patch = associationPatchFromDraft(draft);
  return (
    association.isDefault !== patch.isDefault ||
    association.showOnCreate !== patch.showOnCreate ||
    association.isRequired !== patch.isRequired ||
    association.isCustomerFacing !== patch.isCustomerFacing ||
    association.snapshotPolicy !== patch.snapshotPolicy ||
    !jobTypeIdsEqual(association.jobTypeIds ?? [], jobTypeIdsForSave(draft))
  );
}

async function syncRulesForExistingDraft(
  definitionId: string,
  rules: CustomFieldInheritanceRule[],
  draft: EntityUsageDraft,
) {
  const targetRules = rulesForTarget(rules, draft.entityType);
  const primaryRule = targetRules[0] ?? null;

  if (!draft.source) {
    await deleteRules(targetRules);
    return;
  }

  if (primaryRule?.sourceEntityType === draft.source.sourceEntityType) {
    // The source is unchanged, and the source is the ONLY thing this UI lets a
    // user set on a rule — there is no behavior selector (COPY_ON_CREATE is the
    // only behavior the API accepts) and no priority input (NOT-191, still
    // outstanding). So there is nothing to patch, and the patch that used to
    // fire here carried no user intent at all: it re-sent the hydrated behavior
    // and flattened priority to 0.
    //
    // That was not harmless. `entityUsageDraftsFromMeta` hydrates a legacy
    // ALWAYS_SYNC row verbatim, so on a definition whose primary rule is legacy
    // and whose priority is non-zero, editing only the label re-sent
    // { behavior: 'ALWAYS_SYNC' } — which the API has rejected since ALWAYS_SYNC
    // was deprecated on 2026-06-11 (73932340e). The rename 400d. When the draft
    // HAD been coerced (by `normalizeDrafts`, which runs on usage edits only),
    // the same patch instead silently migrated the stored row to COPY_ON_CREATE
    // and reset its priority.
    //
    // Sending nothing fixes the 400 and both silent writes. When the priority
    // input lands, that is when this should start patching again — with an
    // actual user intent behind it.
    await deleteRules(targetRules.filter((rule) => rule.id !== primaryRule.id));
    return;
  }

  await deleteRules(targetRules);
  await createRule(definitionId, draft);
}

async function createRule(definitionId: string, draft: EntityUsageDraft) {
  if (!draft.source) return;
  await apiPost<CustomFieldInheritanceRule, CreateInheritanceRuleDto>(
    operationsClient,
    customFieldEndpoints.inheritanceRules(),
    {
      fieldDefinitionId: definitionId,
      sourceEntityType: draft.source.sourceEntityType,
      targetEntityType: draft.entityType,
      // COPY_ON_CREATE is the only behavior the API will create; a draft's
      // behavior field may still carry a hydrated legacy ALWAYS_SYNC.
      behavior: 'COPY_ON_CREATE',
      priority: 0,
    },
  );
}

async function deleteRules(rules: CustomFieldInheritanceRule[]) {
  for (const rule of rules) {
    await apiDelete<void>(operationsClient, customFieldEndpoints.inheritanceRule(rule.id));
  }
}

function rulesForTarget(
  rules: CustomFieldInheritanceRule[],
  targetEntityType: CustomFieldEntityType,
): CustomFieldInheritanceRule[] {
  return rules
    .filter((rule) => rule.targetEntityType === targetEntityType)
    .sort((a, b) => a.priority - b.priority);
}
